import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Fade,
  Container,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import { Person, Phone, Email, Lock, Visibility, VisibilityOff, Language } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BASE_URL ||
  'http://localhost:5000';

const translations = {
  en: { signUp: 'Sign Up', createAccount: 'Create your account to get started', fullName: 'Full Name', emailAddress: 'Email Address', phoneNumber: 'Phone Number', password: 'Password', terms: 'I agree to the ', termsLink: 'Terms of Service', privacyLink: 'Privacy Policy', createAccountBtn: 'Create Account', alreadyAccount: 'Already have an account? Sign in' },
  yo: { signUp: 'Forukọsilẹ', createAccount: 'Ṣẹda akọọlẹ rẹ lati bẹrẹ', fullName: 'Orukọ kikun', emailAddress: 'Adirẹsi Imeeli', phoneNumber: 'Nọmba foonu', password: 'Ọrọigbaniwọle', terms: 'Mo gba ', termsLink: 'Awọn ofin iṣẹ', privacyLink: 'Ilana Aṣiri', createAccountBtn: 'Ṣẹda akọọlẹ', alreadyAccount: 'Ṣe o ni akọọlẹ tẹlẹ? Wọle' },
  ig: { signUp: 'Debanye aha', createAccount: 'Mepụta akaụntụ gị iji malite', fullName: 'Aha zuru ezu', emailAddress: 'Adreesị Email', phoneNumber: 'Nọmba ekwentị', password: 'Okwuntughe', terms: 'Anabata m ', termsLink: 'Ọnọdụ ọrụ', privacyLink: 'Nzuzo', createAccountBtn: 'Mepụta akaụntụ', alreadyAccount: 'Ị nwere akaụntụ? Banye' },
  ha: { signUp: 'Yi Rajista', createAccount: 'Ƙirƙiri asusunka don fara', fullName: 'Cikakken suna', emailAddress: 'Adireshin Imel', phoneNumber: 'Lambar waya', password: 'Kalmar sirri', terms: 'Na yarda da ', termsLink: 'Sharuɗɗan Sabis', privacyLink: 'Sirrin Bayani', createAccountBtn: 'Ƙirƙiri Asusun', alreadyAccount: 'Tuni kana da asusu? Shiga' },
};

export default function Register() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [language, setLanguage] = useState('en');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();
  const t = translations[language];

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setSnackbar({ open: true, message: 'Please accept the Terms and Privacy Policy.', severity: 'warning' });
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/students/register`, {
        name, email, phone_number: phoneNumber, password,
      });
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
      }
      if (res.data?.student) {
        localStorage.setItem('student', JSON.stringify(res.data.student));
      }
      setSnackbar({ open: true, message: 'Registration successful! Redirecting to course selection...', severity: 'success' });
      setTimeout(() => navigate('/Courseselection'), 1200);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Registration failed.', severity: 'error' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="mt-20">
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              borderRadius: '24px',
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Left Section - Image Hidden on Mobile */}
            <Box
              sx={{
                flex: 1.2,
                position: 'relative',
                minHeight: { xs: 0, md: '400px' },
                overflow: 'hidden',
                display: { xs: 'none', md: 'block' }, // Hide on mobile
              }}
            >
              <img
                src="af013cb32ec8306a4e3ccec2eb6d4037b8f01b18.jpg"
                alt="Register"
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.05)', transition: 'transform 0.5s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1.05)'}
              />
              <Box sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 100%)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: 4,
              }}>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, textShadow: '2px 2px 6px rgba(0,0,0,0.5)' }}>
                  Peer2Learn
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', mt: 1, fontWeight: 500 }}>
                  Join the future of collaborative learning.
                </Typography>
              </Box>
            </Box>

            {/* Right Section - Form */}
            <Box sx={{ flex: 1, p: { xs: 4, md: 6 }, position: 'relative' }}>
              {/* Language Selector Floating */}
              <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  size="small"
                  variant="standard"
                  disableUnderline
                  IconComponent={Language}
                  sx={{ 
                    bgcolor: 'rgba(0,0,0,0.05)', 
                    px: 2, py: 0.5, 
                    borderRadius: '20px',
                    fontSize: '0.8rem'
                  }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="yo">Yoruba</MenuItem>
                  <MenuItem value="ig">Igbo</MenuItem>
                  <MenuItem value="ha">Hausa</MenuItem>
                </Select>
              </Box>

              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '', mb: 1 }}>
                  
                  <span className='text-gray-700'>{t.signUp}</span>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.createAccount}
                </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                {[ 
                  { label: t.fullName, val: name, set: setName, icon: <Person />, type: 'text' },
                  { label: t.emailAddress, val: email, set: setEmail, icon: <Email />, type: 'email' },
                  { label: t.phoneNumber, val: phoneNumber, set: setPhoneNumber, icon: <Phone />, type: 'tel' }
                ].map((field, idx) => (
                  <TextField
                    key={idx}
                    fullWidth
                    label={field.label}
                    value={field.val}
                    onChange={(e) => field.set(e.target.value)}
                    margin="normal"
                    type={field.type}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ color: '#3a86ff' }}>{field.icon}</InputAdornment>,
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '16px',
                        transition: '0.3s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }
                      } 
                    }}
                  />
                ))}

                <TextField
                  fullWidth
                  label={t.password}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin="normal"
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start" sx={{ color: '#3a86ff' }}><Lock /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                />

                <FormControlLabel
                  control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />}
                  label={
                    <Typography variant="caption" color="text.secondary">
                      {t.terms} <Link to="/terms" style={{ color: '#3a86ff' }}>{t.termsLink}</Link>
                    </Typography>
                  }
                  sx={{ mt: 1, mb: 2 }}
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !acceptedTerms}
                  sx={{
                    py: 2,
                    borderRadius: '16px',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '1rem',
                    background: 'linear-gradient(90deg, #3a86ff, #4361ee)',
                    boxShadow: '0 10px 20px rgba(58, 134, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 15px 30px rgba(58, 134, 255, 0.4)',
                      background: 'linear-gradient(90deg, #4361ee, #3a86ff)',
                    }
                  }}
                >
                  {isSubmitting ? 'Processing...' : t.createAccountBtn}
                </Button>

                <Typography textAlign="center" sx={{ mt: 3 }}>
                  <Link to="/login" style={{ textDecoration: 'none', color: '#666', fontSize: '0.9rem' }}>
                    {t.alreadyAccount}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
    </div>
  );
}